/**
 * synthesisStatus.js
 * Shared constants + helpers for rendering interview synthesis state (Item 1.3b).
 *
 * The backend (per Item 1.3a) writes `interview_sessions.synthesis_status` as one of:
 *   'pending' | 'complete' | 'failed'
 * Plus NULL on legacy rows that predate the column (PROD sessions 1 and 3 at the
 * time of Item 1.3b shipping). Frontend logic must defensively map NULL → COMPLETE
 * before any other status check, or those legacy rows surface as "failed-ish."
 *
 * This module ships with Item 1.3b's β3 hybrid scope (CompanyDetail session-list
 * pill + InterviewWizard Phase 6 review). It deliberately stops at constants +
 * label/copy helpers — NOT a shared React component. Two consumers isn't enough
 * to design a UI abstraction; we extract one when surfaces 3 and 4 are picked
 * up and we can see what shape they actually need.
 */

export const SYNTHESIS_STATUS = Object.freeze({
  PENDING: 'pending',
  COMPLETE: 'complete',
  FAILED: 'failed',
});

/**
 * Normalize a raw synthesis_status from the API response.
 * NULL / undefined / unknown values map to COMPLETE (defensive — legacy rows
 * predate the column and should not surface as failed).
 */
export function normalizeSynthesisStatus(raw) {
  if (raw === SYNTHESIS_STATUS.PENDING) return SYNTHESIS_STATUS.PENDING;
  if (raw === SYNTHESIS_STATUS.FAILED) return SYNTHESIS_STATUS.FAILED;
  return SYNTHESIS_STATUS.COMPLETE;
}

/**
 * Shared copy strings — kept here so the two β3 surfaces speak the same
 * language. When a third surface joins, this is the right place to add a
 * variant rather than copy-paste a new string.
 */
export const SYNTHESIS_COPY = Object.freeze({
  FAILED_SHORT: 'Synthesis failed',
  FAILED_INTERVIEWEE: 'Synthesis failed. The session lead can re-engage the interviewee to retry. You can close this view safely.',
  FAILED_ADMIN_TOOLTIP: 'AI synthesis failed for this session. The interviewee can re-engage to retry (no admin action required).',
  COMPLETE_EMPTY_FALLBACK: 'No summary available. You can continue to the next phase.',
});
